import React from 'react';

import { WellServeAdministration } from 'services/wellServeAdministration';
import { SERVICE_RESPONSE_STATUS, GLOBAL_LABELS } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as successOverlay from 'utils/successOverlay';
import { get, find, isNil, isEmpty, cloneDeep, omit, isEqual } from 'lodash';

// React view component to render
import { callCategoriesAndReasons as CallCategoriesAndReasonsView } from 'ui/views/widgets/callCategoriesAndReasons';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CallCategoriesAndReasons } from 'ui/containers/CallCategoriesAndReasons';
 *
 * // render
 * <CallCategoriesAndReasons propName='propValue'/>
 */
export class CallCategoriesAndReasons extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        log.debug( 'CallCategoriesAndReasons.constructor()' );

        super( props );

        this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
            activeSection: 'callCategoriesAndReasons',
            isFormValid: false,
            values: {},
            errorInfo: null,
            keepFieldsDisabled: true,
            newReasonsList: []
        };

        this.wellServeAdministration = new WellServeAdministration();

        this.__bind();

        this.fetchComponentData();
    }

    /**
     * @desc Bind functions
     */
     __bind() {
        log.debug( 'CallCategoriesAndReasons.__bind()' );

        this.handleSectionChange = this.handleSectionChange.bind( this );
        this.handleFieldChange = this.handleFieldChange.bind( this );
        this.handleSubmit = this.handleSubmit.bind( this );
        this.handleCancel = this.handleCancel.bind( this );
        this.handleReasonAddition = this.handleReasonAddition.bind( this );
        this.handleReasonDeletion = this.handleReasonDeletion.bind( this );
    }

    /**
     * @desc composes field values
     */
    composeFieldValues( { activeSection, componentData = this.state.componentData } = {} ) {
        log.debug( 'CallCategoriesAndReasons.composeFieldValues()' );

        const _fields = get( componentData, `sections.${activeSection}.fields`, [] );

        const intialFieldValues = {};

        _fields.forEach( _field => {
            
            intialFieldValues [ _field.name ] = 'reasons' === _field.name ? [] : '';
        } );

        return intialFieldValues;
    }

	/**
	 * @desc Fetch component response.
	 */
     async fetchComponentData() {
		log.debug( 'CallCategoriesAndReasons.fetchComponentData()' );

		// cancel ongoing request
		this.wellServeAdministration.cancel();

		// get status and service data
		const {
			status,
			data,
		} = await this.wellServeAdministration.fetchComponentData( {
			url: this.props.widget.url,
		} );

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				componentData: data,
                values: this.composeFieldValues( { activeSection: 'callCategoriesAndReasons', componentData: data } )
			} );
		}
    }

	/**
	 * @desc Handles section change
	 */
    handleSectionChange( name ) {
        log.debug( 'CallCategoriesAndReasons.handleSectionChange()' );

        this.setState( {
            activeSection: name,
            isFormValid: false,
            values: this.composeFieldValues( { activeSection: name } ),
            errorInfo: null,
            keepFieldsDisabled: 'createReasons' === name ? false : true,
            newReasonsList: []
        } );

    }

	/**
	 * @desc Handles submission
	 */
	async handleSubmit() {
		log.debug( 'WellViewAdministration.handleSubmit()' );

		// cancel ongoing request
		this.wellServeAdministration.cancel();

		// show page level preloader
		pageLevelPreloader.show();

        // finds active section's submit button
		const saveAction = find( this.state.componentData.sections[ this.state.activeSection ].actions, {
			name: 'submit',
		} );

        let _requestData = cloneDeep( this.state.values );

        if ( 'createCallCategory' === this.state.activeSection ) {

            _requestData = omit( _requestData, [ 'reasonName' ] );

            _requestData.newReasonsList = [ ...this.state.newReasonsList ];
        }

		// get status and service data
		const {
			status,
            data
		} = await this.wellServeAdministration.submitData( {
			url: saveAction.url,
			data: {
				context: {},
				data: {
                    ..._requestData
                },
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				errorInfo: get( data, 'title', null )
			} );

		} else {
			successOverlay.open( {
				message: get( data, 'title', {} )
			} );

			this.setState( {
					isFetching: true,
					networkStatus: undefined,
                    activeSection: 'callCategoriesAndReasons',
                    isFormValid: false,
                    values: {},
                    errorInfo: null,
                    keepFieldsDisabled: true
			},
				() => this.fetchComponentData()
			);
		}
    }

	/**
	 * @desc Handles form field change
	 */
    handleFieldChange( payload ) {
        log.debug( 'CallCategoriesAndReasons.handleFieldChange()' );

        const { values, field } = payload;

        let isFormValid = true;

        let keepFieldsDisabled = true;
        
        if ( 'category' === get( field, 'name', '' ) ) {

            const _categoryOption = find( get( field, 'options', [] ), { value: get( values, 'category', null ) } );

            const reasons = get( _categoryOption, 'options', [] ).map( option => {

                return get( option, 'value' );
            } );

            values.reasons = reasons;

            // whenever new category selected
            // form will be invalid, unless new selection
            // from  reasons list happens
            isFormValid = false;

        } else if ( 'reasons' === get( field, 'name', '' ) ) {

            const _categoryOptionSelected = find( get( this.state, 'componentData.options.categoryAndReasons', [] ), { value: get( values, 'category', null ) } );

            const alreadyExistReasons = get( _categoryOptionSelected, 'options', [] ).map( option => {

                return get( option, 'value' );
            } );

            const _reasons = get( values, 'reasons', [] );

            const uniqueReasons = [];

            // remove duplicates if any
            _reasons.forEach( _item => {

                if ( !uniqueReasons.includes( _item ) ) {
                    uniqueReasons.push( _item );
                }
            } );

            values.reasons = uniqueReasons;

            // compares if values selected and already
            // present in category are same
            isFormValid = !isEqual( alreadyExistReasons.sort(), uniqueReasons.sort() );
        }

        // checks for all values to evaluate isFormValid and keepFieldsDisabled flags
        for ( const _fieldKey in values ) {

            if ( isFormValid && ( isEmpty( values[ _fieldKey ] ) || isNil( values[ _fieldKey ] ) ) ) {

                isFormValid = false;
            }

            // evaluate disabled attr for fields
            if ( keepFieldsDisabled && ( !isEmpty( values[ _fieldKey ] ) && !isNil( values[ _fieldKey ] ) ) ) {

                keepFieldsDisabled = false;
            }
        }

        if ( 'createCallCategory' === this.state.activeSection ) {

            const _categoryName = get( values, 'categoryName', '' );

            if ( !isEmpty( _categoryName ) ) {

                const _reasons = get( values, 'reasons', [] );

                const _newReasonsList = get( this.state, 'newReasonsList', [] );

                if ( !isEmpty( _reasons ) || !isEmpty( _newReasonsList ) ) {

                    isFormValid = true;
                }

            }
        }

        // If section is createReasons, should be false always
        if ( 'createReasons' === this.state.activeSection ) {

            keepFieldsDisabled = false;
        }

        this.setState( {
            values: values,
            isFormValid,
            errorInfo: null,
            keepFieldsDisabled
        } );

    }

	/**
	 * @desc Handles reason addition
	 */
     async handleReasonAddition( payload ) {
        log.debug( 'CallCategoriesAndReasons.handleReasonAddition()' );

		// cancel ongoing request
		this.wellServeAdministration.cancel();

		// show page level preloader
		pageLevelPreloader.show();

        const _requestData = {
            reasonName: get( this.state, 'values.reasonName', '' )
        };

		// get status and service data
		const {
			status,
            data
		} = await this.wellServeAdministration.submitData( {
			url: payload.url,
			data: {
				context: {},
				data: {
                    ..._requestData
                },
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				errorInfo: get( data, 'title', null )
			} );

		} else {

            const newReasonsList = [ ...this.state.newReasonsList ];

            newReasonsList.push( get( this.state, 'values.reasonName', '' ) );

            let isFormValid = this.state.isFormValid;

            if ( 'createCallCategory' === this.state.activeSection ) {

                const _categoryName = get( this.state.values, 'categoryName', '' );
    
                if ( !isEmpty( _categoryName ) ) {
    
                    isFormValid = !isEmpty( newReasonsList );
    
                }
            }

			this.setState( {
                    values: {
                        ...this.state.values,
                        reasonName: ''
                    },
                    newReasonsList,
                    isFormValid
			} );
		}
    }

	/**
	 * @desc Handles cancellation
	 */
     handleCancel() {
        log.debug( 'CallCategoriesAndReasons.handleCancel()' );

        this.setState( {
            activeSection: 'callCategoriesAndReasons',
            isFormValid: false,
            values: this.composeFieldValues( { activeSection: 'callCategoriesAndReasons' } ),
            errorInfo: null,
            keepFieldsDisabled: true,
            newReasonsList: []
        } );
    }

	/**
	 * @desc Handles reason deletion
	 */
     handleReasonDeletion( reasonName ) {
        log.debug( 'CallCategoriesAndReasons.handleReasonDeletion()' );

        const _newReasonsList = [ ...this.state.newReasonsList ];

        const _filteredList = _newReasonsList.filter( _reason => {

            return _reason !== reasonName;
        } );

        let isFormValid = this.state.isFormValid;

        const _categoryName = get( this.state.values, 'categoryName', '' );

        const _reasons = get( this.state.values, 'reasons', [] );

        if ( !isEmpty( _categoryName ) ) {

            isFormValid = !isEmpty( _filteredList ) || !isEmpty( _reasons ); 

        }

        this.setState( {
            newReasonsList: _filteredList,
            isFormValid
        } );
    
     }

    /**
	 * @desc to do clean-ups
	 */
    componentWillUnmount() {
        log.debug( 'CallCategoriesAndReasons.componentWillUnmount()' );

        // cancel ongoing request
		this.wellServeAdministration.cancel();
    }

    /**
     * @desc Render a React view component
     */
    render() {
        log.debug( 'CallCategoriesAndReasons.render()' );

        return (
            <CallCategoriesAndReasonsView
                { ...this.state }
                onSectionChange = { this.handleSectionChange }
                onChangeCallBack = { this.handleFieldChange }
                handleSubmit = { this.handleSubmit }
                handleCancel = { this.handleCancel }
                handleReasonAddition = { this.handleReasonAddition }
                handleReasonDeletion = { this.handleReasonDeletion }
            />
        );
    }
}

// set display name
CallCategoriesAndReasons.displayName = 'CallCategoriesAndReasons';

// set default props
CallCategoriesAndReasons.defaultProps = {};

// prop types
CallCategoriesAndReasons.propTypes = {};
